import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Container, Typography, Box, Link, Divider } from "@mui/material";
import { ArrowRightAlt, OpenInNew } from "@mui/icons-material";
import { gradient, appBackgroundGradient } from "./helpers/utils";
import HeaderSection from "./helpers/HeaderSection";
import { getBrandAssets } from "../utils/assetHelpers";
import { useBrand } from "../context/BrandContext";

function HomePage() {
  const { config } = useBrand();
  const { features, social, brand } = config || {};
  const assets = getBrandAssets(brand?.slug);
  const logoUrl = assets.avaNov25;

  // Internal navigation links based on features
  const internalLinks = [
    {
      text: "bookings inquiries",
      href: "/booking",
      show: true, // Always show booking
    },
    {
      text: "check availability",
      href: "/availability",
      show: features.availability,
    },
    {
      text: "gallery / reels / pictures",
      href: "/gallery",
      show: features.gallery,
    },
    {
      text: "reviews",
      href: "/reviews",
      show: features.reviews,
    },
    {
      text: "upcoming events / calendar",
      href: "/calendar",
      show: features.events,
    },
    {
      text: "audio & music session previews",
      href: "/audio",
      show: features.audio,
    },
    {
      text: "sound engineering services / av technician",
      href: "/sound-engineering-services",
      show: features.soundEngineering,
    },
    {
      text: "info / expect / process",
      href: "/info-expect-process",
      show: features.infoExpectProcess,
    },
  ].filter((item) => item.show);

  // External social links based on brand configuration
  const socialLinks = [];

  if (social.instagram) {
    socialLinks.push({
      text: "instagram.com",
      href: social.instagram.url,
    });
  }

  if (social.mixcloud) {
    socialLinks.push({
      text: "mixcloud.com / dj mixes",
      href: social.mixcloud.url,
    });
  }

  if (social.soundcloud) {
    socialLinks.push({
      text: "soundcloud.com / dj mixes",
      href: social.soundcloud.url,
    });
  }

  if (social.youtube) {
    socialLinks.push({
      text: "youtube channel / dj mixes",
      href: social.youtube.url,
    });
  }

  if (social.housemusic) {
    socialLinks.push({
      text: "Lounge & House Music Project",
      href: social.housemusic.url,
    });
  }

  // Combine all links
  const linkItems = [...internalLinks, ...socialLinks];

  return (
    <Container
      maxWidth={false}
      sx={{
        textAlign: "center",
        background: appBackgroundGradient,
        backgroundSize: "400% 400%",
        animation: `${gradient} 10s ease infinite`,
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        color: "white",
        padding: "2rem 0 2rem 0",
        fontSize: "calc(0.75rem + 2vmin)",
        paddingTop: "5rem",
      }}
    >
      <Box
        spacing={2}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          width: "100%",
          maxWidth: "40rem",
          p: "1rem",
          m: "0",
        }}
      >
        <HeaderSection
          mainTitle={brand.name.toUpperCase()}
          logo={logoUrl}
          avatarAlt={brand.name.toUpperCase()}
          showBackButton={false}
        />
        <Box
          sx={{
            width: "100%",
            maxWidth: "35rem",
            mt: 2,
          }}
        >
          {linkItems?.map((item, index) => {
            const isExternal = /^https?:\/\//.test(item?.href);
            const isInstagram = item.text === "instagram.com";

            return (
              <React.Fragment key={index}>
                {isInstagram && (
                  <Divider
                    sx={{
                      my: 1,
                      borderColor: "rgba(255, 255, 255, 0.2)",
                      "&:before, &:after": {
                        borderColor: "rgba(255, 255, 255, 0.2)",
                      },
                    }}
                  />
                )}
                <Link
                  component={isExternal ? "a" : RouterLink}
                  to={isExternal ? undefined : item.href}
                  href={isExternal ? item.href : undefined}
                  underline="none"
                  color="inherit"
                  target={isExternal ? "_blank" : undefined}
                  rel={isExternal ? "noopener noreferrer" : undefined}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    textDecoration: "none",
                    padding: "1rem",
                    width: "100%",
                    boxSizing: "border-box",
                    borderRadius: "1rem",
                    "&:hover": {
                      background: "linear-gradient(-45deg, #44A08D, #093637)",
                      backgroundSize: "400% 400%",
                      animation: `${gradient} 10s ease infinite`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ textAlign: "left", whiteSpace: "normal" }}
                    >
                      {item.text}
                    </Typography>
                    {isExternal && (
                      <OpenInNew sx={{ fontSize: "1rem", opacity: 0.7 }} />
                    )}
                  </Box>
                  <ArrowRightAlt />
                </Link>
              </React.Fragment>
            );
          })}
        </Box>
      </Box>
    </Container>
  );
}

export default HomePage;
